import { ToggleState, useToggleState } from '@react-stately/toggle'
import { useCheckbox } from '@react-aria/checkbox'
import { AriaCheckboxProps, ToggleProps } from '@react-types/checkbox'
import { createContext, PropsWithChildren, useContext, useRef } from 'react'
import { CheckboxChecked } from 'blaze-ui/icons'
import CheckboxLight from 'blaze-ui/icons/20/CheckboxLight'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import Styles from './Checkbox.module.scss'
import { useFocusRing } from '@react-aria/focus'
import cx from 'classnames'
import { AriaLabelingProps } from '@react-types/shared'
import { mergeProps, useId } from '@react-aria/utils'

interface Props extends AriaCheckboxProps {
  size?: number
  color?: string
}

const CheckboxContext = createContext<ToggleState & { isFocusVisible: boolean }>({
  isSelected: false,
  isFocusVisible: false,
  toggle: () => {},
  setSelected: () => {},
})

export const Label = ({
  children,
  className,
  ...props
}: PropsWithChildren<ToggleProps & AriaLabelingProps & { className?: string }>) => {
  const state = useToggleState(props)
  const ref = useRef(null)
  const { inputProps } = useCheckbox(props, state, ref)
  const { isFocusVisible, focusProps } = useFocusRing(props)
  const id = useId()

  return (
    <CheckboxContext.Provider value={{ ...state, isFocusVisible }}>
      <VisuallyHidden>
        <input {...mergeProps(inputProps, focusProps)} ref={ref} id={id} />
      </VisuallyHidden>
      <label className={className} htmlFor={id} onMouseDown={(e) => e.preventDefault()}>
        {children}
      </label>
    </CheckboxContext.Provider>
  )
}

interface BoxProps {
  size?: number
  color?: string
  className?: string
}

export const Box = ({ size, color, className }: BoxProps) => {
  const { isFocusVisible, isSelected } = useContext(CheckboxContext)
  return (
    <div
      className={cx(Styles.checkbox, className, {
        [Styles.focusVisible]: isFocusVisible,
        [Styles.selected]: isSelected,
      })}
    >
      {isSelected ? <CheckboxChecked size={size} color={color} /> : <CheckboxLight size={size} color={color} />}
    </div>
  )
}

export const Checkbox = ({ size = 20, color, ...props }: Props) => {
  return (
    <Label {...props}>
      <div className={Styles.label}>
        <Box size={size} color={color} />
        {props.children}
      </div>
    </Label>
  )
}
